import { connect } from "react-redux";
import moment from "moment-timezone";
import React, { Fragment, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";

import Button from "@/components/UI/Button";
import { getPatientProfile } from "@/redux/actions/auth";
import images from "@/utils/images";
import { isCareTeamRestricted } from "@/utils/scheduleUtils";
import {
  ListBody,
  ListHeader,
  ListItem,
  ListTitleBar,
  TitleBarItem,
} from "@/components/UI/ListView";
import { useCurrentUTCTime } from "@/utils/hooks/useCurrentTime";

import ScheduleDialog from "../CancellationPolicy/ScheduleDialog";

import CancellationPolicy from "./CancellationPolicy";
import { CancellationPolicyPopup } from "./CancellationPolicyPopup";
import ContextMenu from "./ContextMenu";
import ListLoader from "./ListLoader";
import ScheduleListItemDesktop from "./SheduleListItemDesktop";
import ScheduleListItemMobile from "./SheduleListItemMobile";
import SchedulingUnavailable from "./SchedulingUnavailable";
import SchedulingWaitlisted from "./SchedulingWaitlisted";

import "./styles.scss";

const AvailableSchedulesList = (props) => {
  let { deleteShedule, fetchUpdatedSchedules, isAddNewDisabled, scheduleLoaded, schedules } = props;
  const [popUpData, setPopUpData] = useState(null); // shows and hide three dots menu pop up
  const [scheduleToBeDeleted, setScheduleToBeDeleted] = useState(null); // sets the data of schedule to be deleted.
  const [type, setType] = useState("");
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const { currentTime } = useCurrentUTCTime({ interval: 5000 });

  const documentClickHandler = function (e) {
    let target = e.target;
    let isClickFromInside = target.closest(".ASCH__Desktop-context-menu,.ASCH__Mobile-context-menu")
      ? true
      : false;

    if (popUpData && !isClickFromInside) {
      setPopUpData(null);
    }
  };

  /**
   * TODO: optimise useEffect for attachment/detachment of click listener.
   * Currently event listener is attached and detached on every popUpData change
   * */
  useEffect(() => {
    document.addEventListener("click", documentClickHandler);

    return () => {
      document.removeEventListener("click", documentClickHandler);
    };
  }, [popUpData, props.profile.assessment_status]);

  const getPrettyTimeDetails = (scheduleTimestamp) => {
    let localScheduledDateTime = moment.utc(scheduleTimestamp, "YYYY-MM-DD HH:mm").local();
    let scheduledDate = localScheduledDateTime.format("DD MMM YYYY");
    let scheduledTime = localScheduledDateTime.format("HH:mm");
    let hour = scheduledTime.split(":")[0];
    let is_AM = hour < 12;
    let isMorning = hour >= 0 && hour <= 11;
    let isAfternoon = hour >= 12 && hour <= 16;
    let isEvening = hour >= 17 && hour <= 24;
    let isToday = localScheduledDateTime.isSame(new Date(), "day");

    return {
      is_AM,
      isAfternoon,
      isEvening,
      isMorning,
      isToday,
      scheduledDate,
      scheduledTime,
    };
  };

  const addNewSheduleHandler = () => {
    props.history.push("/app/schedule/assign?mode=new");
  };

  /**
   * Delete schedule related handlers
   */
  const showDeletePopUp = (sheduleDetails) => {
    setPopUpData(null);
    setType("cancel");
    setScheduleToBeDeleted(sheduleDetails);
  };

  const dismissDeletePopUp = () => {
    setScheduleToBeDeleted(null);
  };

  const deleteScheduleHandler = async (formData) => {
    try {
      /**
       * deleteSchedule() from props below
       * is the http call via `sharedRequests.remove_appointment()` in parent component.
       */
      setType("");
      setScheduleToBeDeleted(null);
      const callId = formData.callId;
      let response = callId && (await deleteShedule(callId, formData));

      fetchUpdatedSchedules();
      props.getPatientProfile();
    } catch (err) {
      console.log(err);
    }
  };

  const editScheduleHandler = ({ allotted_time, callId, provider_username }) => {
    props.history.push(
      "/app/schedule/assign?mode=edit" +
        "&provider_id=" +
        provider_username +
        "&call_id=" +
        callId +
        "&allotted_time=" +
        allotted_time,
    );
  };

  const editSchedule = (provider_username, callId, allotted_time, sheduleDetails) => {
    setPopUpData(null);
    setType("reschedule");
    setScheduleToBeDeleted(sheduleDetails);
  };

  const getClickedSheduleDetails = (callId) => {
    let filteredShedule = schedules.filter((shd) => {
      return shd.callId == callId;
    })[0]; // array is returned, so we select '0' indexed item

    return constructListItemData(filteredShedule);
  };

  const menuClickHandler = (e, callId, provider_username) => {
    let rect = e.target.getBoundingClientRect();
    let popUpLeft;

    if (window.innerWidth > 991 && window.innerWidth < 1300) {
      popUpLeft = rect.left - 120;
    } else {
      popUpLeft = rect.left + 35;
    }
    let popUpTop = rect.top - 40;
    let contextMenuSheduleDetails = getClickedSheduleDetails(callId);

    setPopUpData({
      actions: {
        delete: { caption: "Cancel", handler: showDeletePopUp },
        edit: { caption: "Reschedule", handler: editSchedule },
      },
      position: { left: popUpLeft, top: popUpTop },
      sheduleDetails: contextMenuSheduleDetails,
    });
  };

  const onInfoClickHandler = () => {
    setShowCancellationPolicy(!showCancellationPolicy);
  };

  const renderControls = () => {
    return (
      <div className="d-none d-lg-flex" style={{ height: 43 }}>
        {scheduleLoaded && !isSchedulingUnavailable() && (
          <Button
            className="Btn Btn--pri Btn-sm"
            disabled={isAddNewDisabled}
            onClick={addNewSheduleHandler}
          >
            Add New
          </Button>
        )}
      </div>
    );
  };

  // A utility function to generate List item data
  const constructListItemData = (schedule) => {
    const {
      allotted_time,
      callId,
      current_time,
      event_type,
      patient_username,
      provider_avatar,
      provider_name,
      provider_role,
      provider_username,
      timestamp,
    } = schedule;
    const email = props.profile?.email;
    const { is_AM, isAfternoon, isEvening, isMorning, isToday, scheduledDate, scheduledTime } =
      getPrettyTimeDetails(timestamp);

    return {
      allotted_time,
      callId,
      configs: props.configs,
      current_time,
      currentTime,
      email,
      event_type,
      is_AM,
      isAfternoon,
      isEvening,
      isMorning,
      isToday,
      menuClickHandler,
      patient_username,
      provider_avatar,
      provider_name,
      provider_role,
      provider_username,
      scheduledDate,
      scheduledTime,
      timestamp,
    };
  };

  let listItems = (
    <Fragment>
      {schedules &&
        schedules.map((schedule) => {
          let ListItemData = constructListItemData(schedule);

          return (
            <ListItem key={schedule.callId}>
              {/* shown only on mobile devices */}
              <ScheduleListItemMobile {...ListItemData} />
              {/* shown only on desktop */}
              <ScheduleListItemDesktop {...ListItemData} />
            </ListItem>
          );
        })}
    </Fragment>
  );

  const MobileAddBtnClass = isAddNewDisabled
    ? "ASCH__ShedulesEmpty-Add position-fixed tw:justify-center align-items-center d-flex d-lg-none ASCH__ShedulesEmpty-Add--disable"
    : "ASCH__ShedulesEmpty-Add position-fixed tw:justify-center align-items-center d-flex d-lg-none ASCH__ShedulesEmpty-Add--enable";

  const submitHandler = type === "cancel" ? deleteScheduleHandler : editScheduleHandler;

  const isSchedulingUnavailable = () => {
    const { assessment_config, assessment_status, is_wait_listed } = props?.profile || {};
    const { skip_a5_for_referral } = assessment_config || {};
    const isWaitlisted =
      is_wait_listed &&
      props?.profile?.care_team_members
        ?.map((member) => isCareTeamRestricted(member["type"], props.profile))
        ?.reduce((prevMember, currMember) => prevMember && currMember, true);

    if (skip_a5_for_referral) {
      return false;
    }

    return !assessment_status || isWaitlisted;
  };

  const isSchedulingRestricted = () => {
    return props.profile?.is_scheduling_restricted;
  };

  const renderSchedulingUnavailableComponent = () => {
    const { assessment_status, is_wait_listed } = props?.profile || {};
    const isWaitlisted =
      is_wait_listed &&
      props.profile?.care_team_members?.reduce((prevVal, member) => {
        return prevVal && isCareTeamRestricted(member["type"], props.profile);
      }, true);

    if (!assessment_status) {
      return <SchedulingUnavailable />;
    } else if (isWaitlisted) {
      return <SchedulingWaitlisted />;
    }
  };

  return (
    <>
      <div className="position-relative">
        <CancellationPolicy onInfoClickHandler={onInfoClickHandler} />

        <ListBody>
          <ListHeader className="d-none d-lg-block" render={renderControls} title="Schedule" />

          {scheduleLoaded && isSchedulingUnavailable() && renderSchedulingUnavailableComponent()}

          {scheduleLoaded && !isSchedulingUnavailable() && (
            <>
              <ListTitleBar>
                <TitleBarItem classes="col-1" text="" />
                <TitleBarItem classes="col-2" text="Name" />
                <TitleBarItem classes="col-2" text="Specialist" />
                <TitleBarItem classes="col-2" text="Date" />
                <TitleBarItem classes="col-1" text="Time" />
                <TitleBarItem classes="col-1" text="" />
                <TitleBarItem classes="col-1" text="" />
              </ListTitleBar>
              <>{!schedules ? <ListLoader /> : listItems}</>
            </>
          )}
        </ListBody>

        {popUpData && (
          <ContextMenu
            actions={popUpData.actions}
            isEditDisabled={isSchedulingRestricted()}
            position={popUpData.position}
            sheduleDetails={popUpData.sheduleDetails}
          />
        )}
        {scheduleToBeDeleted && (
          <ScheduleDialog
            activeScheduleDetails={scheduleToBeDeleted}
            cancelHandler={dismissDeletePopUp}
            submitHandler={submitHandler}
            type={type}
          />
        )}
      </div>

      {showCancellationPolicy && (
        <CancellationPolicyPopup
          onClickHandler={onInfoClickHandler}
          showCancellationPolicy={showCancellationPolicy}
        />
      )}
      <Button
        className={MobileAddBtnClass}
        disabled={isAddNewDisabled}
        onClick={addNewSheduleHandler}
      >
        <img className="img-fluid" src={images("./icons/add-white-btn.svg")} />
      </Button>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, { getPatientProfile })(withRouter(AvailableSchedulesList));
